// packages/console/src/hooks/useUrlUploads.ts
//
// Cloud-to-cloud (from-URL) uploads. Unlike a chunked drag/drop upload, the
// bytes move SERVER-SIDE: POST /v1/uploads/from-url returns 202 immediately and
// the upload service streams the remote source into storage in the background,
// surviving a client refresh. This hook tracks those transfers so they show in
// the transfer queue, polls GET /v1/uploads/{id} to completion, and on landing
// hands the object off (startTranscode) exactly like a drag/drop landing.
//
// Refresh-rediscovery: in-flight from-URL uploads are persisted to localStorage
// (just the id + label), so a reload re-adds them to the queue and resumes
// polling — the server keeps fetching regardless. Nothing is fabricated: an
// entry only lands when the server reports COMPLETED with an object key (R10).

import { useCallback, useEffect, useRef, useState } from "react";
import { uploadFromUrl, getUpload, cancelUpload } from "../api/upload";
import { ApiError } from "../api/http";
import type { UploadView } from "../upload/engine";

const LS_KEY = "aether.urlUploads.v1";
const POLL_MS = 2500;
const POLL_DEADLINE_MS = 40 * 60_000; // give a large cloud-to-cloud pull room

interface Tracked {
  uploadId: string;
  file: string;
  sizeBytes: number;
}

export interface UseUrlUploads {
  urlUploads: UploadView[];
  addUrl: (url: string) => void;
  // cancel aborts an in-flight cloud-to-cloud fetch. id is the transfer's view
  // id (`url:<uploadId>`); it DELETEs the server session, stops polling, and
  // drops the row so the queue reflects the abort immediately.
  cancel: (id: string) => void;
  busy: boolean;
}

// viewFor builds an UploadView-compatible entry so from-URL transfers render in
// the same transfer queue as chunked uploads.
function viewFor(t: Tracked, state: UploadView["state"], extra?: Partial<UploadView>): UploadView {
  return {
    id: `url:${t.uploadId}`,
    uploadId: t.uploadId,
    file: t.file,
    sizeBytes: t.sizeBytes,
    state,
    chunkSizeBytes: 0,
    chunkStates: [],
    doneChunks: 0,
    chunkCount: 0,
    streams: [],
    liveStreams: 0,
    bytesMoved: 0,
    throughputBytesPerSec: 0,
    retries: 0,
    etaSeconds: null,
    errorMessage: null,
    objectKey: null,
    sha256: null,
    ...extra,
  };
}

function readPersisted(): Tracked[] {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
function writePersisted(list: Tracked[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {
    /* storage full/blocked: rediscovery is best-effort, the fetch still runs */
  }
}

export function useUrlUploads(onLanded: (objectKey: string, file: string, sha256: string | null) => void): UseUrlUploads {
  const [urlUploads, setUrlUploads] = useState<UploadView[]>([]);
  const [busy, setBusy] = useState(false);
  const timers = useRef<Map<string, number>>(new Map());
  // landedIds guards onLanded to fire at most once per uploadId: an async tick can
  // still be past its await when the next interval tick fires, so both could reach
  // the COMPLETED branch. The synchronous check-and-add here makes the second a
  // no-op (otherwise the second would create a duplicate, billable transcode job).
  const landedIds = useRef<Set<string>>(new Set());
  const onLandedRef = useRef(onLanded);
  onLandedRef.current = onLanded;

  const dropPersisted = (uploadId: string) => writePersisted(readPersisted().filter((p) => p.uploadId !== uploadId));
  const stopPoll = (uploadId: string) => {
    const h = timers.current.get(uploadId);
    if (h !== undefined) {
      clearInterval(h);
      timers.current.delete(uploadId);
    }
  };
  const setEntry = (uploadId: string, v: UploadView) =>
    setUrlUploads((cur) => cur.map((e) => (e.uploadId === uploadId ? v : e)));

  const poll = useCallback((t: Tracked) => {
    if (timers.current.has(t.uploadId)) return;
    const deadline = Date.now() + POLL_DEADLINE_MS;
    const tick = async () => {
      try {
        const s = await getUpload(t.uploadId);
        if ((s.state === "COMPLETED" || s.state === "ASSEMBLED") && s.objectKey) {
          stopPoll(t.uploadId);
          dropPersisted(t.uploadId);
          setEntry(t.uploadId, viewFor(t, "landed", { objectKey: s.objectKey, sha256: s.sha256 ?? null }));
          // Fire the landing exactly once, even if two ticks race to COMPLETED.
          if (!landedIds.current.has(t.uploadId)) {
            landedIds.current.add(t.uploadId);
            onLandedRef.current(s.objectKey, t.file, s.sha256 ?? null);
          }
        } else if (s.state === "CANCELLED") {
          stopPoll(t.uploadId);
          dropPersisted(t.uploadId);
          setEntry(t.uploadId, viewFor(t, "error", { errorMessage: "the URL fetch failed" }));
        } else if (Date.now() > deadline) {
          stopPoll(t.uploadId);
          dropPersisted(t.uploadId);
          setEntry(t.uploadId, viewFor(t, "error", { errorMessage: "timed out waiting for the URL fetch" }));
        }
      } catch (err) {
        // A 404 means the session is gone (cleaned up); stop. Other errors are
        // transient network blips — keep polling until the deadline.
        if (err instanceof ApiError && err.status === 404) {
          stopPoll(t.uploadId);
          dropPersisted(t.uploadId);
          setEntry(t.uploadId, viewFor(t, "error", { errorMessage: "the URL upload could not be found" }));
        }
      }
    };
    const h = window.setInterval(() => void tick(), POLL_MS);
    timers.current.set(t.uploadId, h);
    void tick();
  }, []);

  const track = useCallback(
    (t: Tracked) => {
      setUrlUploads((cur) => (cur.some((e) => e.uploadId === t.uploadId) ? cur : [...cur, viewFor(t, "uploading")]));
      poll(t);
    },
    [poll],
  );

  const addUrl = useCallback(
    (url: string) => {
      const u = url.trim();
      if (!u) return;
      setBusy(true);
      void (async () => {
        try {
          const res = await uploadFromUrl(u);
          const t: Tracked = { uploadId: res.uploadId, file: res.filename || u, sizeBytes: res.sizeBytes };
          writePersisted([...readPersisted().filter((p) => p.uploadId !== t.uploadId), t]);
          track(t);
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : "network error";
          window.alert(`Could not start the URL fetch: ${msg}`);
        } finally {
          setBusy(false);
        }
      })();
    },
    [track],
  );

  // cancel aborts a from-URL fetch: the view id is `url:<uploadId>`, so strip
  // the prefix, DELETE the server session, stop the poll, drop the persisted
  // rediscovery record, and remove the row from the queue. The DELETE is best-
  // effort (fire-and-forget); the local teardown happens regardless so the row
  // never lingers as uncancellable.
  const cancel = useCallback((id: string) => {
    const uploadId = id.startsWith("url:") ? id.slice(4) : id;
    stopPoll(uploadId);
    dropPersisted(uploadId);
    setUrlUploads((cur) => cur.filter((e) => e.uploadId !== uploadId));
    void cancelUpload(uploadId).catch(() => {
      /* the session may already be gone; local teardown already happened */
    });
  }, []);

  // Rehydrate in-flight from-URL uploads after a refresh and resume polling.
  useEffect(() => {
    readPersisted().forEach(track);
    const t = timers.current;
    return () => {
      t.forEach((h) => clearInterval(h));
      t.clear();
    };
  }, [track]);

  return { urlUploads, addUrl, cancel, busy };
}
